// FILE: src/pages/errors/games/PongTwoPaddlesGame.jsx
import React, { useEffect, useRef, useState } from "react";
import winImg from "../../../assets/jenny.png.png";
import { awardAchievement } from "../../../components/utils/achievements"; // важно: именно components/utils

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const WIN_SCORE = 3;
// правильный путь к файлу в public: /ses.mp3 (или с PUBLIC_URL)
const WIN_MUSIC = (process.env.PUBLIC_URL ? process.env.PUBLIC_URL : "") + "/ses.mp3";

const PongTwoPaddlesGame = ({ width = 720, height = 420 }) => {
  const cvsRef = useRef(null);
  const rafRef = useRef(0);
  const lastT = useRef(performance.now());
  const lastMove = useRef(performance.now());

  const [phase, setPhase] = useState("idle"); // idle | playing | over | win
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem("pong_best") || 0));
  const bestRef = useRef(best);
  const [lives, setLives] = useState(3);
  const livesRef = useRef(3);

  const paddleH = 12;
  const player = useRef({ x: width / 2, w: 120, h: paddleH, y: height - 28, vx: 0 });
  const cpu = useRef({ x: width / 2, w: 120, h: paddleH, y: 16 });
  const ball = useRef({ x: width / 2, y: height / 2, r: 8, vx: 0, vy: 0, speed: 300 });

  const winAudioRef = useRef(null);

  useEffect(() => { scoreRef.current = score; }, [score]);
  useEffect(() => { bestRef.current = best; }, [best]);

  useEffect(() => {
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const cvs = cvsRef.current;
    cvs.width = Math.floor(width * dpr);
    cvs.height = Math.floor(height * dpr);
    cvs.style.width = `${width}px`;
    cvs.style.height = `${height}px`;
    cvs.getContext("2d").setTransform(dpr, 0, 0, dpr, 0, 0);
    draw();
  }, [width, height]);

  const setBallByAngle = (angleRad, speed, upwards) => {
    const dirY = upwards ? -1 : +1;
    ball.current.vx = Math.sin(angleRad) * speed;
    ball.current.vy = Math.cos(angleRad) * speed * dirY;
    ball.current.speed = speed;
  };
  const normalizeSpeed = () => {
    const b = ball.current;
    const s = Math.hypot(b.vx, b.vy) || 1;
    b.vx = (b.vx / s) * b.speed;
    b.vy = (b.vy / s) * b.speed;
  };
  const wallReflect = () => { ball.current.vx = -ball.current.vx; normalizeSpeed(); };

  const serve = (towardsPlayer = true) => {
    const b = ball.current;
    b.x = width / 2; b.y = height / 2;
    const MAX_SERVE_ANG = 0.55;
    const angle = (Math.random() * 2 - 1) * MAX_SERVE_ANG;
    const speed = 300 + Math.random() * 50;
    setBallByAngle(Math.abs(angle), speed, !towardsPlayer);
    b.vx *= Math.sign(Math.random() - 0.5) || 1;
  };

  const initRound = () => {
    player.current = { x: width / 2, w: 120, h: paddleH, y: height - 28, vx: 0 };
    cpu.current    = { x: width / 2, w: 120, h: paddleH, y: 16 };
    setScore(0); scoreRef.current = 0;
    setLives(3); livesRef.current = 3;
    ball.current.speed = 300;
    serve(true);
  };

  const startGame = () => {
    if (phase === "playing") return;
    // стопнем победную музыку, если играли до этого
    if (winAudioRef.current) {
      try {
        winAudioRef.current.pause();
        winAudioRef.current.currentTime = 0;
      } catch {}
    }
    initRound(); setPhase("playing");
  };

  const finishWithBest = (nextPhase) => {
    setPhase(nextPhase);
    setBest((b) => {
      const nb = Math.max(b, scoreRef.current);
      localStorage.setItem("pong_best", String(nb));
      return nb;
    });
  };

  // победа: запускаем музыку и достижение
  useEffect(() => {
    if (phase === "win") {
      try {
        const a = winAudioRef.current;
        if (a) {
          // гарантируем перезагрузку и проигрывание
          a.pause();
          a.currentTime = 0;
          // если браузер требует жест — он был (игра стартует по клику)
          const p = a.play();
          if (p && p.catch) p.catch(() => {});
        }
      } catch {}
      try { awardAchievement("win-404"); } catch {}
    }
  }, [phase]);

  useEffect(() => () => {
    try { winAudioRef.current?.pause(); } catch {}
    cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    if (phase !== "playing") { draw(); return; }
    let mounted = true; lastT.current = performance.now();
    const step = (t) => {
      if (!mounted) return;
      const dt = (t - lastT.current) / 1000;
      lastT.current = t;
      update(dt); draw();
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { mounted = false; cancelAnimationFrame(rafRef.current); };
  }, [phase]);

  useEffect(() => {
    const move = (clientX) => {
      const rect = cvsRef.current.getBoundingClientRect();
      const raw = clientX - rect.left;
      const p = player.current;
      const now = performance.now();
      const dt = Math.max(0.016, (now - lastMove.current) / 1000);
      const target = clamp(raw, p.w / 2 + 4, width - p.w / 2 - 4);
      p.vx = (target - p.x) / dt; p.x = target; lastMove.current = now;
      if (phase !== "playing") draw();
    };
    const onPointer = (e) => move(e.clientX ?? e.touches?.[0]?.clientX ?? 0);
    const cvs = cvsRef.current;
    cvs.addEventListener("pointermove", onPointer);
    cvs.addEventListener("touchmove", onPointer, { passive: true });
    return () => { cvs.removeEventListener("pointermove", onPointer); cvs.removeEventListener("touchmove", onPointer); };
  }, [width, phase]);

  const onCanvasPointerDown = () => startGame();

  const predictXAtY = (x, y, vx, vy, targetY) => {
    if (vy >= 0) return width / 2;
    const t = (targetY - y) / vy;
    let hitX = x + vx * t;
    const W = width, period = 2 * W;
    hitX = ((hitX % period) + period) % period;
    if (hitX > W) hitX = period - hitX;
    return hitX;
  };

  const update = (dt) => {
    const p = player.current, a = cpu.current, b = ball.current;

    b.x += b.vx * dt; b.y += b.vy * dt;

    if (b.x - b.r < 0) { b.x = b.r; wallReflect(); }
    if (b.x + b.r > width) { b.x = width - b.r; wallReflect(); }

    const targetX = predictXAtY(b.x, b.y, b.vx, b.vy, a.y + a.h + b.r + 2);
    const aiDelay = 0.12, aiMax = 290;
    const delta = targetX - a.x;
    const step = clamp(delta * aiDelay, -aiMax * dt, aiMax * dt);
    a.x = clamp(a.x + step, a.w / 2 + 4, width - a.w / 2 - 4);

    if (b.vy < 0 && b.y - b.r <= a.y + a.h && Math.abs(b.x - a.x) <= a.w / 2 + b.r) {
      const offset = clamp((b.x - a.x) / (a.w / 2 + b.r), -1, 1);
      let ang = Math.abs(offset) * 1.1; if (ang < 0.25) ang = 0.25;
      setBallByAngle(ang, Math.min(b.speed * 1.04, 620), false);
      b.vx += (a.x - targetX) * 0.02; normalizeSpeed();
      b.y = a.y + a.h + b.r + 0.1;
    }

    if (b.vy > 0 && b.y + b.r >= p.y && Math.abs(b.x - p.x) <= p.w / 2 + b.r) {
      const offset = clamp((b.x - p.x) / (p.w / 2 + b.r), -1, 1);
      let ang = Math.abs(offset) * 1.15; if (ang < 0.23) ang = 0.23;
      setBallByAngle(ang, Math.min(b.speed * 1.055, 660), true);
      b.vx += p.vx * 0.12; normalizeSpeed();
      b.y = p.y - b.r - 0.1;
    }

    if (b.y + b.r < 0) {
      const ns = scoreRef.current + 1; scoreRef.current = ns; setScore(ns);
      if (ns >= WIN_SCORE) { finishWithBest("win"); return; }
      ball.current.speed = Math.min(ball.current.speed * 1.06, 700);
      serve(true); return;
    }

    if (b.y - b.r > height) {
      livesRef.current = Math.max(0, livesRef.current - 1); setLives(livesRef.current);
      if (livesRef.current > 0) { ball.current.speed = Math.max(300, ball.current.speed * 0.92); serve(true); }
      else { finishWithBest("over"); }
    }
  };

  const draw = () => {
    const ctx = cvsRef.current.getContext("2d");
    const b = ball.current;

    const g = ctx.createLinearGradient(0, 0, 0, height);
    g.addColorStop(0, "#0b1120"); g.addColorStop(1, "#0a0f1a");
    ctx.fillStyle = g; ctx.fillRect(0, 0, width, height);

    ctx.globalAlpha = 0.08; ctx.strokeStyle = "#fff"; ctx.lineWidth = 1;
    for (let x = 40.5; x < width; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke(); }
    for (let y = 40.5; y < height; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke(); }
    ctx.globalAlpha = 1;

    ctx.strokeStyle = "rgba(255,255,255,.12)"; ctx.setLineDash([6, 6]);
    ctx.beginPath(); ctx.moveTo(0, height / 2); ctx.lineTo(width, height / 2); ctx.stroke(); ctx.setLineDash([]);

    const drawPaddle = (x, y, w, h, c1, c2) => {
      ctx.save(); ctx.translate(x - w / 2, y);
      ctx.fillStyle = c1; ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = c2; ctx.fillRect(2, 2, w - 4, h - 4);
      ctx.restore();
    };
    drawPaddle(player.current.x, player.current.y, player.current.w, player.current.h, "rgba(167,139,250,.6)", "rgba(167,139,250,.9)");
    drawPaddle(cpu.current.x, cpu.current.y, cpu.current.w, cpu.current.h, "rgba(236,72,153,.55)", "rgba(236,72,153,.9)");

    ctx.save(); ctx.translate(b.x, b.y);
    ctx.shadowColor = "rgba(167,139,250,0.55)"; ctx.shadowBlur = 16;
    const grad = ctx.createRadialGradient(0, 0, 2, 0, 0, b.r + 4);
    grad.addColorStop(0, "#a78bfa"); grad.addColorStop(1, "#ec4899");
    ctx.fillStyle = grad; ctx.beginPath(); ctx.arc(0, 0, b.r, 0, Math.PI * 2); ctx.fill();
    ctx.restore();

    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.font = "12px ui-sans-serif, system-ui, -apple-system";
    ctx.textAlign = "right";
    ctx.fillText(`Очки: ${scoreRef.current}`, width - 10, 18);
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.fillText(`Рекорд: ${bestRef.current}`, width - 10, 34);
  };

  const overlayText =
    phase === "idle"
      ? "ЛКМ/тап — старт. Веди тачпадом/мышью по горизонтали."
      : "Закончились жизни. ЛКМ/тап — сыграть ещё";

  return (
    <div className="relative mx-auto w-full max-w-[820px] rounded-2xl border border-white/10 bg-slate-900/50 shadow-2xl overflow-hidden">
      <div className="pointer-events-none absolute left-3 top-2 z-10 flex items-center gap-1 text-xl select-none">
        {Array.from({ length: 3 }).map((_, i) => (
          <span key={i} className={i < lives ? "opacity-90" : "opacity-25"} aria-hidden="true">💀</span>
        ))}
      </div>

      {/* победная музыка */}
      <audio ref={winAudioRef} src={WIN_MUSIC} preload="auto" />

      <canvas
        ref={cvsRef}
        width={width}
        height={height}
        onPointerDown={onCanvasPointerDown}
        className="block w-full h-auto cursor-none select-none"
        title="ЛКМ/тап — старт. Веди тачпадом/мышью по горизонтали — нижняя платформа."
      />

      {phase !== "playing" && phase !== "win" && <Overlay text={overlayText} onPointerDown={startGame} />}
      {phase === "win" && <WinOverlay onPointerDown={startGame} score={scoreRef.current} img={winImg} />}
    </div>
  );
};

const Overlay = ({ text, onPointerDown }) => (
  <div role="button" tabIndex={0} onPointerDown={onPointerDown} onKeyDown={(e) => (e.key === "Enter" || e.code === "Space") && onPointerDown()} className="absolute inset-0 grid place-items-center text-center text-white/85 bg-black/35 backdrop-blur-[2px] cursor-pointer select-none">
    <div className="space-y-2"><div className="text-lg font-semibold">{text}</div></div>
  </div>
);

const WinOverlay = ({ onPointerDown, score, img }) => (
  <div role="button" tabIndex={0} onPointerDown={onPointerDown} onKeyDown={(e) => (e.key === "Enter" || e.code === "Space") && onPointerDown()} className="absolute inset-0 grid place-items-center text-center bg-black/55 backdrop-blur-md cursor-pointer select-none">
    <div className="relative max-w-2xl w-[min(92vw,760px)] mx-auto p-5 rounded-2xl border border-violet-500/30 bg-gradient-to-b from-violet-600/20 to-fuchsia-600/10 shadow-[0_0_40px_rgba(139,92,246,.35)]">
      <div className="flex items-center gap-5">
        <div className="text-left">
          <div className="text-3xl font-extrabold tracking-wide">🚀 Победа! <span className="text-violet-300">3/3</span></div>
          <div className="mt-2 text-sm text-white/80">Ты переиграл ИИ-модератора — портал SES открылся и орёт хитяру 🎵<br/>Очки: <span className="text-white">{score}</span>. Кликни, чтобы запустить новую волну.</div>
          <div className="mt-4 inline-block rounded-lg px-3 py-1 text-sm bg-white/10 border border-white/15">ЛКМ/тап — играть снова</div>
        </div>
        <img src={img} alt="Победа!" className="h-40 w-auto object-contain drop-shadow-[0_0_18px_rgba(139,92,246,.45)]" onError={(e)=> (e.currentTarget.style.display="none")} decoding="async" draggable={false}/>
      </div>
    </div>
  </div>
);

export default PongTwoPaddlesGame;
